/**
 * The three pool search filters on web: sport, league and pool type
 * (ISSUE-199 R4). Every list's first option is "All", which sends no filter
 * key at all. Picking a sport clears the league, because a league only lives
 * inside one sport, and a league left behind from the previous sport would
 * narrow the search to nothing.
 *
 * The web twin of `apps/mobile/src/pools/poolSearchFilters.ts`. `apps/app`
 * cannot import from `apps/mobile`, so the rules are re-declared here rather
 * than shared, the same way `createPoolDraft.ts` re-declares the create-pool
 * rules. The sport-to-league derivation is this app's own
 * `poolEligibleLeagueOptions`, not the mobile module's `leaguesForSport`,
 * because `apps/app` has no copy of the latter.
 *
 * server-resolves-client-renders.mdc: these keys travel to `/pool/search` in
 * the request body, and `services/pool-api/src/repo/pools.ts` puts each one it
 * receives on the Mongo filter. Nothing here re-filters a row the server
 * already returned.
 */
import type { typeSelectItem } from "@sportsmet/ui";

import {
  poolEligibleLeagueOptions,
  type typeCatalogSport,
} from "../createPool/createPoolDraft";

/**
 * The option key that means "do not narrow by this at all". `Select` has no
 * empty-string value (Radix reserves it), so the absence of a filter needs a
 * key of its own, and it is translated back to `null` on the way out.
 */
export const POOL_SEARCH_FILTER_ALL = "all";

export const POOL_SEARCH_FILTER_COPY = {
  sportLabel: "Sport",
  leagueLabel: "League",
  typeLabel: "Pool type",
  allOption: "All",
} as const;

export type typePoolSearchFilters = {
  sportKey: string | null;
  leagueKey: string | null;
  contestType: string | null;
};

export const EMPTY_POOL_SEARCH_FILTERS: typePoolSearchFilters = {
  sportKey: null,
  leagueKey: null,
  contestType: null,
};

export function filterKeyToValue(key: string): string | null {
  return key === POOL_SEARCH_FILTER_ALL ? null : key;
}

export function filterValueToKey(value: string | null): string {
  return value ?? POOL_SEARCH_FILTER_ALL;
}

/**
 * Choosing a sport always clears the league, including when the fan chooses
 * the same sport again. A league key from the old sport survives no filter on
 * the server: `filter.sportKey` and `filter.leagueKey` are ANDed, so the pair
 * would match zero pools and the fan would read that as a broken search.
 */
export function choosePoolSearchSport(
  filters: typePoolSearchFilters,
  key: string,
): typePoolSearchFilters {
  return {
    ...filters,
    sportKey: filterKeyToValue(key),
    leagueKey: null,
  };
}

export function choosePoolSearchLeague(
  filters: typePoolSearchFilters,
  key: string,
): typePoolSearchFilters {
  return {
    ...filters,
    leagueKey: filterKeyToValue(key),
  };
}

export function choosePoolSearchType(
  filters: typePoolSearchFilters,
  key: string,
): typePoolSearchFilters {
  return {
    ...filters,
    contestType: filterKeyToValue(key),
  };
}

/**
 * Only sports that hold at least one pool-eligible league are offered. A pool
 * is created in a pool-eligible league and nowhere else
 * (`poolEligibleLeagueOptions` is what the create flow shows), so a sport with
 * none of them can only ever narrow the search to zero rows.
 */
export function poolSearchSportOptions(
  sports: readonly typeCatalogSport[] | null,
  allLabel: string,
): typeSelectItem[] {
  const items: typeSelectItem[] = [
    { value: POOL_SEARCH_FILTER_ALL, label: allLabel },
  ];
  const offered = new Set(
    poolEligibleLeagueOptions(sports).map((option) => option.sportKey),
  );
  for (const sport of sports ?? []) {
    if (!offered.has(sport.sportKey)) continue;
    items.push({ value: sport.sportKey, label: sport.name });
  }
  return items;
}

/**
 * Empty until a sport is chosen. A league list spanning every sport would be
 * long, and picking from it would leave the sport filter disagreeing with it.
 * The pane draws no league control while this is empty.
 */
export function poolSearchLeagueOptions(
  sports: readonly typeCatalogSport[] | null,
  sportKey: string | null,
  allLabel: string,
): typeSelectItem[] {
  if (sportKey === null) return [];
  const items: typeSelectItem[] = [
    { value: POOL_SEARCH_FILTER_ALL, label: allLabel },
  ];
  for (const option of poolEligibleLeagueOptions(sports)) {
    if (option.sportKey !== sportKey) continue;
    items.push({ value: option.leagueKey, label: option.label });
  }
  return items;
}

export function poolSearchTypeOptions(
  contestTypes: readonly { contestType: string; name: string }[],
  allLabel: string,
): typeSelectItem[] {
  const items: typeSelectItem[] = [
    { value: POOL_SEARCH_FILTER_ALL, label: allLabel },
  ];
  for (const type of contestTypes) {
    items.push({ value: type.contestType, label: type.name });
  }
  return items;
}
